/** Offline catch-up: the live sim fast-forwarded in coarse steps (GDD §6). */

import { advance } from './tick';
import { offlineHours } from './rooms';
import { sellPrice } from './economy';
import { fleetStats } from './stats';
import type { Emit } from './events';
import type { GameState } from './state';

/** Shorter absences are simply played through by the live loop. */
export const OFFLINE_MIN_SECONDS = 15;

export interface OfflineSummary {
  seconds: number;
  /** The absence ran past the offline cap (the Archive raises it). */
  capped: boolean;
  capHours: number;
  /** GC earned plus the sell value of everything added to the hold. */
  value: number;
  sc: number;
  /** Rover missions that came home while you were away. */
  rovers: number;
  /** Void Shards from a Challenge that ended while you were away. */
  vs: number;
  discovered: string[];
  /** Hulls earned while away. */
  ships: string[];
}

function holdValue(state: GameState): number {
  const stats = fleetStats(state);
  return Object.entries(state.run.hold).reduce((sum, [id, qty]) => sum + qty * sellPrice(state, stats, id), 0);
}

export function simulateOffline(state: GameState, seconds: number): OfflineSummary {
  const cap = offlineHours(state) * 3600;
  const span = Math.min(cap, Math.max(0, seconds));
  const summary: OfflineSummary = { seconds: span, capped: seconds > cap, capHours: cap / 3600, value: 0, sc: 0, vs: 0, rovers: 0, discovered: [], ships: [] };
  const before = state.run.gc + holdValue(state);
  const tally: Emit = (e) => {
    if (e.kind === 'challenge') summary.vs += e.vs;
    else if (e.kind === 'ship') summary.ships.push(e.id);
    else summary.sc += e.sc;
    if (e.kind === 'discovered') summary.discovered.push(e.id);
    if (e.kind === 'rover') summary.rovers += 1;
  };
  const step = span > 4 * 3600 ? 2 : 1;
  for (let t = 0; t < span; t += step) advance(state, Math.min(step, span - t), tally);
  summary.value = Math.max(0, state.run.gc + holdValue(state) - before);
  return summary;
}
